/**
 * ELPHELYS - LUXURY HAIR STRAND BACKGROUND ANIMATION
 * A sophisticated Canvas-based flowing hair animation that creates
 * an organic, premium atmosphere for a luxury wig e-commerce site.
 *
 * Architecture:
 * - Simplex noise for organic, natural movement
 * - Multi-depth layers with parallax
 * - Catmull-Rom spline interpolation for silky-smooth curves
 * - Gold shimmer highlights traveling root-to-tip
 * - Scroll-reactive wind for subtle interactivity
 * - Floating golden dust particles for luxury feel
 * - FPS-throttled rendering with visibility-pause
 * - Full prefers-reduced-motion support
 */

(function () {
  'use strict';

  // =========================================================================
  // SIMPLEX NOISE - lightweight seeded 2D
  // =========================================================================

  const GRAD2 = [[1,1],[-1,1],[1,-1],[-1,-1],[1,0],[-1,0],[0,1],[0,-1]];
  const F2 = 0.5 * (Math.sqrt(3) - 1);
  const G2 = (3 - Math.sqrt(3)) / 6;

  class SimplexNoise {
    constructor(seed) {
      this.perm = new Uint8Array(512);
      const p = new Uint8Array(256);
      for (let i = 0; i < 256; i++) p[i] = i;
      let s = seed || 42;
      for (let i = 255; i > 0; i--) {
        s = (s * 16807) % 2147483647;
        const j = s % (i + 1);
        const tmp = p[i]; p[i] = p[j]; p[j] = tmp;
      }
      for (let i = 0; i < 512; i++) this.perm[i] = p[i & 255];
    }

    noise2D(x, y) {
      const s = (x + y) * F2;
      const i = Math.floor(x + s), j = Math.floor(y + s);
      const t = (i + j) * G2;
      const x0 = x - (i - t), y0 = y - (j - t);
      const i1 = x0 > y0 ? 1 : 0, j1 = x0 > y0 ? 0 : 1;
      const x1 = x0 - i1 + G2, y1 = y0 - j1 + G2;
      const x2 = x0 - 1 + 2 * G2, y2 = y0 - 1 + 2 * G2;
      const ii = i & 255, jj = j & 255;
      let n0 = 0, n1 = 0, n2 = 0;
      let t0 = 0.5 - x0*x0 - y0*y0;
      if (t0 > 0) { t0 *= t0; const g = GRAD2[this.perm[ii + this.perm[jj]] % 8]; n0 = t0*t0*(g[0]*x0+g[1]*y0); }
      let t1 = 0.5 - x1*x1 - y1*y1;
      if (t1 > 0) { t1 *= t1; const g = GRAD2[this.perm[ii+i1+this.perm[jj+j1]] % 8]; n1 = t1*t1*(g[0]*x1+g[1]*y1); }
      let t2 = 0.5 - x2*x2 - y2*y2;
      if (t2 > 0) { t2 *= t2; const g = GRAD2[this.perm[ii+1+this.perm[jj+1]] % 8]; n2 = t2*t2*(g[0]*x2+g[1]*y2); }
      return 70 * (n0 + n1 + n2);
    }
  }

  // =========================================================================
  // CATMULL-ROM SPLINE HELPER
  // =========================================================================

  function catmullRomPoint(p0, p1, p2, p3, t) {
    const t2 = t * t, t3 = t2 * t;
    return {
      x: 0.5 * ((2*p1.x) + (-p0.x+p2.x)*t + (2*p0.x-5*p1.x+4*p2.x-p3.x)*t2 + (-p0.x+3*p1.x-3*p2.x+p3.x)*t3),
      y: 0.5 * ((2*p1.y) + (-p0.y+p2.y)*t + (2*p0.y-5*p1.y+4*p2.y-p3.y)*t2 + (-p0.y+3*p1.y-3*p2.y+p3.y)*t3)
    };
  }

  // =========================================================================
  // CONFIGURATION
  // =========================================================================

  const CONFIG = {
    // Root colors (darker, near scalp)
    rootColors: [
      { r: 42, g: 8, b: 20 },     // near-black bordeaux
      { r: 60, g: 10, b: 28 },    // deep bordeaux
      { r: 92, g: 15, b: 39 },    // bordeaux-dark
      { r: 75, g: 12, b: 32 },    // mid-dark bordeaux
    ],

    // Tip colors (lighter, golden highlights)
    tipColors: [
      { r: 125, g: 21, b: 53 },   // bordeaux-primary
      { r: 155, g: 37, b: 69 },   // bordeaux-medium
      { r: 181, g: 67, b: 90 },   // bordeaux-light
      { r: 201, g: 132, b: 58 },  // gold-primary
      { r: 180, g: 100, b: 50 },  // warm brown-gold
      { r: 160, g: 80, b: 55 },   // auburn
    ],

    // Layers (back-to-front): fewer strands, each more detailed
    layers: [
      { count: 35, speed: 0.25, opacity: 0.075, widthRoot: 2.8, widthTip: 0.6 },
      { count: 25, speed: 0.45, opacity: 0.11, widthRoot: 2.0, widthTip: 0.4 },
      { count: 18, speed: 0.65, opacity: 0.15, widthRoot: 1.4, widthTip: 0.25 },
    ],

    // Motion
    noiseScale: 0.0007,
    noiseSpeed: 0.0003,  // Slower, subtle organic movement
    waveAmplitude: 130,
    windBaseStrength: 0.8, // Calmer default wind
    windBaseFrequency: 0.0004,
    scrollWindMultiplier: 0.8,
    scrollWindDecay: 0.92,

    // Shimmer (gold highlight traveling along strand)
    shimmerSpeed: 0.0005,
    shimmerWidth: 0.12,
    shimmerBoost: 2.5,    // multiplier on alpha at shimmer peak
    shimmerGoldBlend: 0.6, // how much to shift color toward gold at shimmer

    // Strand geometry
    controlPoints: 8,          // lower to save CPU
    interpolationSteps: 6,      // reduced to fix lag while avoiding the worst of the dots
    strandLengthMin: 1.5,
    strandLengthMax: 2.2,

    // Floating particles
    particleCount: 15,
    particleSizeMin: 0.5,
    particleSizeMax: 2.0,
    particleOpacityMin: 0.02,
    particleOpacityMax: 0.08,
    particleSpeed: 0.015,

    // Performance
    targetFPS: 30, // Locked to 30 FPS saves massive battery/CPU
    mobileMultiplier: 0.35,
  };

  // Gold color for shimmer blending
  const GOLD = { r: 224, g: 168, b: 90 };

  // =========================================================================
  // HAIR STRAND
  // =========================================================================

  class HairStrand {
    constructor(w, h, layer, noise) {
      this.noise = noise;
      this.layer = layer;
      this.cp = CONFIG.controlPoints;

      // Anchor position: spread wide horizontally, always start well above the screen
      this.anchorX = Math.random() * w * 1.5 - w * 0.25;
      this.anchorY = -h * 0.5 - Math.random() * h * 0.4;

      // Length
      const lf = CONFIG.strandLengthMin + Math.random() * (CONFIG.strandLengthMax - CONFIG.strandLengthMin);
      this.length = h * lf;

      // Noise offsets
      this.nox = Math.random() * 2000;
      this.noy = Math.random() * 2000;

      // Colors (root and tip)
      this.rootColor = CONFIG.rootColors[Math.floor(Math.random() * CONFIG.rootColors.length)];
      this.tipColor = CONFIG.tipColors[Math.floor(Math.random() * CONFIG.tipColors.length)];

      // Curvature bias
      this.curveBias = (Math.random() - 0.5) * 50;

      // Shimmer phase
      this.shimmerPhase = Math.random() * Math.PI * 2;

      // Control point positions (reused array)
      this.pts = [];
      for (let i = 0; i <= this.cp; i++) this.pts.push({ x: 0, y: 0 });
    }

    update(time, scrollWind) {
      const spd = this.layer.speed;
      const t = time * CONFIG.noiseSpeed * spd;
      const baseWind = Math.sin(time * CONFIG.windBaseFrequency) * CONFIG.windBaseStrength;
      const wind = baseWind + scrollWind;

      for (let i = 0; i <= this.cp; i++) {
        const r = i / this.cp;
        const bx = this.anchorX + this.curveBias * r * r;
        const by = this.anchorY + this.length * r;

        const nx = this.noise.noise2D(
          (bx + this.nox) * CONFIG.noiseScale,
          (by + this.noy) * CONFIG.noiseScale + t
        );
        const ny = this.noise.noise2D(
          (bx + this.nox + 500) * CONFIG.noiseScale,
          (by + this.noy + 500) * CONFIG.noiseScale + t * 0.6
        );

        // Sway increases quadratically from root to tip
        const sway = r * r * CONFIG.waveAmplitude;
        this.pts[i].x = bx + nx * sway + wind * sway * 0.6;
        this.pts[i].y = by + ny * sway * 0.25;
      }
    }

    draw(ctx, time) {
      const layer = this.layer;
      const baseAlpha = layer.opacity;
      const shimmerT = ((time * CONFIG.shimmerSpeed + this.shimmerPhase) % (Math.PI * 2)) / (Math.PI * 2);
      const pts = this.pts;
      const cp = this.cp;
      const steps = CONFIG.interpolationSteps;

      // We draw the strand using Catmull-Rom interpolation between control points
      // Each sub-segment gets its own color/alpha for gradient + shimmer effect
      for (let i = 0; i < cp; i++) {
        const p0 = pts[Math.max(0, i - 1)];
        const p1 = pts[i];
        const p2 = pts[Math.min(cp, i + 1)];
        const p3 = pts[Math.min(cp, i + 2)];

        for (let s = 0; s < steps; s++) {
          const tLocal = s / steps;
          const tNext = (s + 1) / steps;
          const globalRatio = (i + tLocal) / cp;
          const globalRatioNext = (i + tNext) / cp;

          const a = catmullRomPoint(p0, p1, p2, p3, tLocal);
          const b = catmullRomPoint(p0, p1, p2, p3, tNext);

          // Strand thickness: thick at root, thin at tip
          const width = layer.widthRoot + (layer.widthTip - layer.widthRoot) * globalRatio;

          // Color gradient: root to tip
          const cr = this.rootColor;
          const ct = this.tipColor;
          let dr = cr.r + (ct.r - cr.r) * globalRatio;
          let dg = cr.g + (ct.g - cr.g) * globalRatio;
          let db = cr.b + (ct.b - cr.b) * globalRatio;

          // Alpha fades toward tip
          const tipFade = 1 - globalRatio * 0.7;
          let alpha = baseAlpha * tipFade;

          // Shimmer: boost alpha and shift toward gold near shimmer position
          const distSh = Math.abs(globalRatio - shimmerT);
          if (distSh < CONFIG.shimmerWidth) {
            const shimmerIntensity = 1 - distSh / CONFIG.shimmerWidth;
            const si = shimmerIntensity * shimmerIntensity; // ease-in-out feel
            alpha *= 1 + si * (CONFIG.shimmerBoost - 1);
            const gb = si * CONFIG.shimmerGoldBlend;
            dr = dr + (GOLD.r - dr) * gb;
            dg = dg + (GOLD.g - dg) * gb;
            db = db + (GOLD.b - db) * gb;
          }

          ctx.strokeStyle = 'rgba(' + (dr|0) + ',' + (dg|0) + ',' + (db|0) + ',' + alpha.toFixed(4) + ')';
          ctx.lineWidth = width;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
    }
  }

  // =========================================================================
  // FLOATING GOLD PARTICLE
  // =========================================================================

  class GoldParticle {
    constructor(w, h, noise) {
      this.noise = noise;
      this.reset(w, h);
      // Randomize starting position vertically
      this.y = Math.random() * h;
    }

    reset(w, h) {
      this.x = Math.random() * w;
      this.y = -10;
      this.size = CONFIG.particleSizeMin + Math.random() * (CONFIG.particleSizeMax - CONFIG.particleSizeMin);
      this.opacity = CONFIG.particleOpacityMin + Math.random() * (CONFIG.particleOpacityMax - CONFIG.particleOpacityMin);
      this.speed = CONFIG.particleSpeed * (0.5 + Math.random());
      this.nox = Math.random() * 1000;
      this.phase = Math.random() * Math.PI * 2;
      this.pulseSpeed = 0.001 + Math.random() * 0.002;
    }

    update(time, w, h) {
      this.y += this.speed * (time * 0.001 % 1 + 0.5);
      this.x += Math.sin(time * 0.0002 + this.phase) * 0.15;

      if (this.y > h + 20) {
        this.reset(w, h);
      }
    }

    draw(ctx, time) {
      // Pulsing opacity
      const pulse = 0.5 + 0.5 * Math.sin(time * this.pulseSpeed + this.phase);
      const alpha = this.opacity * (0.4 + pulse * 0.6);

      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(224,168,90,' + alpha.toFixed(4) + ')';
      ctx.fill();

      // Soft glow around larger particles
      if (this.size > 1.2) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(224,168,90,' + (alpha * 0.15).toFixed(4) + ')';
        ctx.fill();
      }
    }
  }

  // =========================================================================
  // MAIN CONTROLLER
  // =========================================================================

  class HairBackground {
    constructor() {
      this.canvas = null;
      this.ctx = null;
      this.strands = [];
      this.particles = [];
      this.noise = new SimplexNoise(Date.now());
      this.running = false;
      this.lastFrame = 0;
      this.frameInterval = 1000 / CONFIG.targetFPS;
      this.time = Math.random() * 50000;
      this.resizeTimeout = null;

      // Scroll-reactive wind
      this.scrollWind = 0;
      this.lastScrollY = 0;
    }

    init() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.canvas = document.getElementById('hairAnimationCanvas');
      if (!this.canvas) return;

      this.ctx = this.canvas.getContext('2d');
      if (!this.ctx) return;

      this.resize();
      this.createStrands();
      this.createParticles();
      this.running = true;
      this.lastFrame = performance.now();
      this.lastScrollY = window.scrollY;
      this.animate(this.lastFrame);

      window.addEventListener('resize', this._onResize = () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.handleResize(), 300);
      });

      window.addEventListener('scroll', this._onScroll = () => {
        const dy = window.scrollY - this.lastScrollY;
        this.scrollWind += dy * 0.003 * CONFIG.scrollWindMultiplier;
        this.lastScrollY = window.scrollY;
      }, { passive: true });

      document.addEventListener('visibilitychange', this._onVisibility = () => {
        if (document.hidden) {
          this.running = false;
        } else {
          this.running = true;
          this.lastFrame = performance.now();
          this.animate(this.lastFrame);
        }
      });
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;

      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
      this.canvas.style.width = w + 'px';
      this.canvas.style.height = h + 'px';
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.w = w;
      this.h = h;
    }

    handleResize() {
      this.resize();
      this.createStrands();
      this.createParticles();
    }

    createStrands() {
      this.strands = [];
      const isMobile = window.innerWidth < 768;
      const mul = isMobile ? CONFIG.mobileMultiplier : 1;

      for (const layer of CONFIG.layers) {
        const count = Math.round(layer.count * mul);
        for (let i = 0; i < count; i++) {
          this.strands.push(new HairStrand(this.w, this.h, layer, this.noise));
        }
      }
    }

    createParticles() {
      this.particles = [];
      const isMobile = window.innerWidth < 768;
      const count = Math.round(CONFIG.particleCount * (isMobile ? 0.4 : 1));
      for (let i = 0; i < count; i++) {
        this.particles.push(new GoldParticle(this.w, this.h, this.noise));
      }
    }

    animate(timestamp) {
      if (!this.running) return;
      requestAnimationFrame((ts) => this.animate(ts));

      const delta = timestamp - this.lastFrame;
      if (delta < this.frameInterval) return;
      this.lastFrame = timestamp - (delta % this.frameInterval);

      this.time += delta;

      // Decay scroll wind
      this.scrollWind *= CONFIG.scrollWindDecay;

      this.render();
    }

    render() {
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.w, this.h);

      const sw = this.scrollWind;

      // Draw strands (back to front via layer order in creation)
      for (const strand of this.strands) {
        strand.update(this.time, sw);
        strand.draw(ctx, this.time);
      }

      // Draw particles on top
      for (const p of this.particles) {
        p.update(this.time, this.w, this.h);
        p.draw(ctx, this.time);
      }
    }

    destroy() {
      this.running = false;
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this.canvas) {
        this.canvas.width = 0;
        this.canvas.height = 0;
      }
    }
  }

  // =========================================================================
  // INIT
  // =========================================================================

  let instance = null;

  function start() {
    if (instance) instance.destroy();
    instance = new HairBackground();
    instance.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.ElphelysHairAnimation = {
    restart: start,
    destroy: function() { if (instance) instance.destroy(); }
  };

})();
